import { cloneElement, type ReactElement } from 'react';
import { Flyout, type FlyoutProps } from 'victory';

import type {
  LineGraphData,
  StreamType,
} from '~/modules/artist-roster/components/LineGraph/LineGraph';

const INNER_CIRCLE_RADIUS = 2;
const CURSOR_Y1 = '5%';
const CURSOR_Y2 = '85%';

interface PointData {
  x: string | number;
  y: string | number;
  seriesColor?: string;
}

interface LineGraphFlyoutProps extends FlyoutProps {
  datum?: PointData;
  streamType?: StreamType;
  dataType?: string;
  data?: LineGraphData[];
  lineColors: string[];
  customFlyoutComponent?: ReactElement | undefined;
  customFlyoutOffset?: number[];
}

function LineGraphFlyout(props: LineGraphFlyoutProps) {
  const { lineColors, customFlyoutComponent, customFlyoutOffset, ...rest } =
    props;
  const x = props.x ?? 0;
  const y = props.y ?? 0;
  const xOffset = customFlyoutOffset?.[0] ?? 0;
  const yOffset = customFlyoutOffset?.[1] ?? 0;

  let flyoutComponent;

  if (customFlyoutComponent) {
    const customComponentWithData = cloneElement(customFlyoutComponent, {
      ...rest,
      index: props.index,
      data: props.data,
      streamType: props.streamType,
      lineColors,
    });

    flyoutComponent = (
      <foreignObject
        x={x > 400 ? x - 95 + xOffset : x + 4 + xOffset}
        y={y + yOffset}
        width={1}
        height={1}
        overflow="visible"
      >
        {customComponentWithData}
      </foreignObject>
    );
  } else {
    flyoutComponent = <Flyout {...rest} />;
  }

  return (
    <g>
      {customFlyoutComponent || props.data?.[0]?.[0].label
        ? flyoutComponent
        : null}
      <circle
        cx={x}
        cy={y}
        r={INNER_CIRCLE_RADIUS}
        fill={props.datum?.seriesColor || 'gray'}
      />
      <line
        x1={x}
        x2={x}
        y1={CURSOR_Y1}
        y2={CURSOR_Y2}
        stroke={props.datum?.seriesColor || 'gray'}
        strokeWidth={0.5}
      />
    </g>
  );
}

LineGraphFlyout.displayName = 'LineGraphFlyout';
export { LineGraphFlyout };
